import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import { App, Workspace } from "@common/models";
import { isModuleRoute } from "@common/util/moduleRouteHelper";
import { setShakeSdkWorkspaceMetadata } from "@common/util/shakeSdkUtils";
import { useWorkspaceAppParams } from "@main/pages/IntegrationActivation/helpers/useWorkspaceAppParams";
import { generatePath, useNavigate } from "react-router-dom";

import { useAppContext } from "./App.context";
import useWorkspaceAppsApiConsumer from "../../consumers/useWorkspaceAppsApiConsumer";
import { RoutePaths } from "../../router/config/routePaths";

type AppSelectionContext = {
  selectedApp: App | undefined;
  selectedWorkspace: Workspace | undefined;
};

const initial: AppSelectionContext = {
  selectedApp: undefined,
  selectedWorkspace: undefined,
};

const appSelectionContext = createContext<AppSelectionContext>(initial);

/// Clicking "back" in the browser doesn't cause a full reload.
/// This var makes sure that selected app is not picked until apps are loaded, but React lifecycle dispatch is "too late".
let fetchingAppsOnWSChangeSync = false;

/*
Always keeps workspace and app "global selection" in sync with the current URL.
*/
export const AppSelectionContextProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { loading: appsLoading, apps, workspaces, updatingSelectedWorkspace } = useAppContext();
  const [selection, setSelection] = useState<AppSelectionContext>(initial);
  const { workspaceSlug, appKey } = useWorkspaceAppParams();
  const { fetchAllApps } = useWorkspaceAppsApiConsumer();

  useEffect(() => {
    fetchingAppsOnWSChangeSync = true;
    if (!workspaces.length || updatingSelectedWorkspace) return;
    /// Resolved from the URL and the workspace list, so it doesn't belong inside the state updater.
    /// Keeping it out also keeps its side effects - the "couldn't be accessed" toast and the SDK
    /// metadata - from being replayed if React re-invokes the updater.
    const selectedWorkspace = findWsBySlug(workspaceSlug, workspaces);
    setShakeSdkWorkspaceMetadata(selectedWorkspace);
    setSelection((selection) => ({
      selectedWorkspace,
      /// Workspace and app are consumed as a pair, so an app from the previous workspace must not
      /// survive a workspace change - otherwise requests are built with the new workspace id and
      /// the old app id while the new workspace's apps are still being fetched.
      selectedApp: selection.selectedApp?.team_id === selectedWorkspace?.id ? selection.selectedApp : undefined,
    }));
  }, [workspaces, workspaceSlug, updatingSelectedWorkspace]);

  useEffect(() => {
    if (!selection.selectedWorkspace) return;
    const abortController = new AbortController();
    fetchAllApps(selection.selectedWorkspace.id, abortController.signal)
      .then((apps) => {
        // We want re-direct user to add app screen if there are no apps, consider only module screens
        // Requests are aborted when workspace is changed, we don't want to redirect in that case
        if (isModuleRoute(location.pathname) && (!apps || apps.length <= 0) && !abortController.signal.aborted) {
          if (workspaceSlug) navigate(generatePath(RoutePaths.ADD_APP, { workspaceSlug }));
        }
      })
      .finally(() => {
        setShakeSdkWorkspaceMetadata(selection.selectedWorkspace);
        fetchingAppsOnWSChangeSync = false;
      });
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line
  }, [selection.selectedWorkspace, fetchAllApps]);

  useEffect(() => {
    if (fetchingAppsOnWSChangeSync) return;
    if (appsLoading || !selection?.selectedWorkspace || updatingSelectedWorkspace) return;

    /// `apps` carries no workspace of its own. A non-empty list with nothing for the selected
    /// workspace still belongs to the previous one, so resolving the URL's app against it would
    /// select a foreign app - and wrongly report it as inaccessible.
    const workspaceApps = apps.filter((app) => app.team_id === selection.selectedWorkspace?.id);
    if (apps.length && !workspaceApps.length) return;

    setSelection((selection) => {
      setShakeSdkWorkspaceMetadata(selection.selectedWorkspace);
      return {
        ...selection,
        selectedApp: findAppByKey(appKey, workspaceApps, selection?.selectedWorkspace?.slug ?? ""),
      };
    });
    if (appKey) localStorage.setItem("localAppKey", appKey ?? "");
  }, [appKey, apps, appsLoading, navigate, selection?.selectedWorkspace, updatingSelectedWorkspace]);

  return <appSelectionContext.Provider value={selection}>{children}</appSelectionContext.Provider>;
};

export const useAppSelectionContext = () => {
  return useContext(appSelectionContext);
};

function findAppByKey(appKey: string | undefined, availableApps: App[], wsSlug: string) {
  if (!appKey || location.pathname.includes("administration")) return;

  const pickedApp = availableApps.filter((a) => a.active).find((a) => a.key === appKey);

  if (!pickedApp) {
    displayToast({
      title: "Yikes, that couldn't be accessed",
      content: `Things often get archived and deleted, or maybe an app doesn't belong to ${wsSlug} workspace.`,
    });
  }

  return pickedApp;
}

function findWsBySlug(slug: string | undefined, availableWorkspaces: Workspace[]) {
  if (!slug) return;

  const pickedWorkspace = availableWorkspaces.find((w) => w.slug === slug);

  if (slug !== "home" && slug !== "administration" && slug !== "account" && slug !== "info" && !pickedWorkspace) {
    displayToast({
      title: "Yikes, that couldn't be accessed",
      content: `Things often get archived and deleted, or maybe you're not a member of ${slug} workspace. If it's the latter, ask someone for an invite.`,
      duration: 5000,
    });
  }

  return pickedWorkspace;
}
